import {Inject, Injectable, Optional} from "@angular/core";
import {BehaviorSubject} from "rxjs";
import {FormGroup} from "@angular/forms";
import {AbstractListService, DestroyStreamService} from "../../services";
import {takeUntil} from "rxjs/operators";
import {IntTableSortingService} from "../int-table-sorting";
import {IntFilterPayloadFormatterService} from "./int-filter-payload.service";
import {IIntDataExtractionPayload} from "./type";
import {INT_LIST_DISPLAY_DATA_EXTRACTION_PROTECTED_PARAMS_BETWEEN_UPDATES_TOKEN} from "./int-list-display-data-extraction-protected-params-between-updates.token";


@Injectable()
export class IntListDisplayDataExtractionService<P extends Object, D> {

  filtersPayload$ = new BehaviorSubject<any>({});
  defaultSearchParams: P | null = null;
  datePrefixesFilterList: Array<string> = [];
  filterForm!: FormGroup;

  queryMiddlewareFn: Function | null = null;
  patchMiddlewareFn: Function | null = null;

  firstLoadState: P | null = null;

  replacePropNameForSortPatchMap!: Map<string, string> | undefined;
  replacePropNameForSortSearchMap!: Map<string, string> | undefined;

  excludeParams: Array<string> = [];

  isFiltersHaveBeenApplied$ = new BehaviorSubject(false);

  defaultFiltersState: string | null = null;

  constructor(
    @Optional()
    @Inject(AbstractListService)
    private readonly abstractProvider: AbstractListService<P, D>,
    @Optional()
    @Inject(IntTableSortingService)
    private readonly tableSortingService: IntTableSortingService,
    @Optional()
    @Inject(IntFilterPayloadFormatterService)
    private readonly dateFormatter: IntFilterPayloadFormatterService,
    @Optional()
    @Inject(DestroyStreamService)
    private readonly destroy$: DestroyStreamService,
    @Optional()
    @Inject(INT_LIST_DISPLAY_DATA_EXTRACTION_PROTECTED_PARAMS_BETWEEN_UPDATES_TOKEN)
    private readonly protectedParams: Array<string>
  ) {
    if(!abstractProvider) {
      console.warn(`Please provide [AbstractListService]`)
    }
    if(!tableSortingService) {
      console.warn(`Please provide [TableSortingService]`)
    }
    if(!dateFormatter) {
      console.warn(`Please provide [FilterPayloadFormatterService]`)
    }
    if(!destroy$) {
      console.warn(`Please provide [DestroyStreamService]`)
    }
  }

  init({
     defaultSearchParams,
     datePrefixesFilterList,
     filterForm,
     patchMiddlewareFn,
     queryMiddlewareFn,
     replacePropNameForSortPatchMap,
     replacePropNameForSortSearchMap,
     excludeParams
   }: IIntDataExtractionPayload<P>) {
    this.abstractProvider.init(defaultSearchParams);
    this.datePrefixesFilterList = datePrefixesFilterList || [];
    this.defaultSearchParams = defaultSearchParams;
    if(filterForm) {
      this.filterForm = filterForm;
      this.defaultFiltersState = JSON.stringify(this.filterForm.getRawValue());
    }
    if(excludeParams) {
      this.excludeParams = excludeParams;
    }
    if(queryMiddlewareFn) {
      this.queryMiddlewareFn = queryMiddlewareFn;
    }
    if(patchMiddlewareFn) {
      this.patchMiddlewareFn = patchMiddlewareFn;
    }
    this.replacePropNameForSortPatchMap = replacePropNameForSortPatchMap;
    this.replacePropNameForSortSearchMap = replacePropNameForSortSearchMap;
    this.abstractProvider
      .firstLoadParamsStream$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {

        this.firstLoadState = payload;

        this.updateFiltersState();

        if('sort' in payload) {
          //@ts-ignore
          this.tableSortingService.setListWithFormatting(payload.sort, replacePropNameForSortPatchMap);
        }
      });

    this.tableSortingService.sortingListMap$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.update();
      });

    if(filterForm) {
      filterForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          const paramsPayload = this.useMiddleware(data, queryMiddlewareFn);
          this.isFiltersHaveBeenApplied$.next(this.compareFilters(data));
          this.filtersPayload$.next({...paramsPayload});
        })
    }
  }

  private updateFiltersState(state?: P) {
    const formatData = this.patchMiddlewareFn
      ? this.patchMiddlewareFn(state || this.firstLoadState)
      : this.dateFormatter.transformParamsToPatchData(state || this.firstLoadState, this.datePrefixesFilterList);

    if(this.filterForm && formatData) {

      const {sort, ...excludeSort} = formatData;

      this.filterForm.patchValue({
        ...excludeSort
      });

      const paramsPayload = this.useMiddleware(excludeSort, this.queryMiddlewareFn || undefined);

      this.isFiltersHaveBeenApplied$.next(this.compareFilters(this.filterForm.getRawValue()));
      this.filtersPayload$.next({...paramsPayload});
    }
  }

  updateFiltersInside() {
    const currentQueryState = this.abstractProvider.params;
    this.updateFiltersState(currentQueryState);
  }

  private useMiddleware(data: P, middleware?: Function) {
    return middleware
      ? middleware(data)
      : this.dateFormatter.clearParams(data, this.datePrefixesFilterList);
  }

  private compareFilters(currentFilterState: {[key: string]: unknown}) {
    const isDiff = this.superficialFiltersComparison(
      currentFilterState,
      JSON.parse(this.defaultFiltersState || '') || {}
    );
    return isDiff ? JSON.stringify(currentFilterState) !== this.defaultFiltersState : isDiff;
  }

  private superficialFiltersComparison(o1: {[key: string]: any}, o2: {[key: string]: any}) {
    const keys = Object.keys(o1);
    let isDiff = true;
    keys.forEach((key) => {
      isDiff = isDiff && (!!o1[key] === !!o2[key])
    });
    return !isDiff;
  }

  private excludeParamsFromList(list: P): P {
    const maps = Object.entries(list);
    return maps.reduce((acc, [key, value]) => {
      if(!this.excludeParams.includes(key)) {
        //@ts-ignore
        acc[key] = value;
      }
      return acc;
    }, {} as P)
  }

  getQueryParams(defaultParams = this.defaultSearchParams) {
    const filters = {...this.filtersPayload$.value};
    const sorts = {
      sort: this.tableSortingService.formatListMapToSearch(this.replacePropNameForSortSearchMap)
    };
    return this.excludeParamsFromList({...filters, ...sorts, ...defaultParams});
  }

  update(defaultParams = this.defaultSearchParams) {
    const protectedParams = this.extractProtectedValuesFromCurrentState();
    const state = {...defaultParams, ...protectedParams};
    this.abstractProvider.setParams(this.getQueryParams(state), {clearParams: true});
  }

  protected extractProtectedValuesFromCurrentState() {
    return Object.entries(this.abstractProvider.params).filter(([key, _]) => {
      return this.protectedParams.includes(key);
    }).reduce((acc, [key, value]) => {
      // @ts-ignore
      acc[key] = value;
      return acc;
    }, {} as P)
  }


  clearFilter(fn?: () => void) {
    this.filterForm?.reset();
    this.filtersPayload$.next({});
    fn && fn();

    this.update();
  }
}
